import React, { useEffect, useState } from "react";
import { FloatingLabelInput } from "./FloatingLabelInput";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toastrError, toastrSuccess } from "./../../lib/toastr";
import { useMutation } from "@tanstack/react-query";
import { registerUserNew } from "./JoinNowForm.api";
import { AxiosError, AxiosResponse } from "axios";
import { getDataNew, getIpAddressByIPify } from "./getdata";
import { loginUser } from "components/LoginForm/LoginForm.api";
import { useAuth } from "context/auth-context";
import { ImageComponent } from "components/LoginForm/ImageComponent";
import { JoinNowWithGoogle } from "./JoinNowWithGoogle";
import { TerminateImageComponent } from "components/LoginForm/TerminateImageComponent";
import { useAltcha, Honeypot } from "components/core/Altcha/AltchaWidget";
import { MarketingConsentCheckbox } from "components/core/Consent/MarketingConsentCheckbox";
import { AuthCard, AuthDivider, AuthLayout } from "components/LoginForm/AuthLayout";

// Define the ErrorBody interface to match your error response structure
interface ErrorBody {
  message?: string;
  [key: string]: any;
}

interface formValue {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  language: string,
  timeZone: string,
  country: string,
  state: string,
  currentUrl: string,
  referalId: string,
  sid: string,
  clickid: string,
  geo: string,
  sessionId: string,
  uid: string,
  altcha: string,
}
type LoginPayload = Omit<formValue, "errors">;

export const JoinNowNew = ({ isGuest }: any) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    language: "",
    timeZone: "",
    country: "",
    state: "",
    currentUrl: "",
    referalId: "",
    sid: "",
    clickid: "",
    geo: "",
    sessionId: "",
    uid: "",
    altcha: "",
  });

  const [errors, setErrors] = useState<any>({});
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [countryname, setCountryName] = useState('USA');
  const [ipAddress, setIpAddressName] = useState();
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [contactReason, setContactReason] = useState("");
  // Unchecked for every user, every country, every locale. Not derived from
  // `countryname` or anything else -- pre-ticking is prohibited unconditionally.
  const [marketingConsent, setMarketingConsent] = useState(false);
  const { widget: altchaWidget, solve: solveAltcha } = useAltcha();

  useEffect(() => {
    const initialize = async () => {
      await getDataNew(setIpAddressName, setCountryName, setForm, form, searchParams);
      const ipAddress = await getIpAddressByIPify();
      if (ipAddress) {
        setIpAddressName(ipAddress);
      }
    };
    initialize();
  }, []);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [field]: e.target.value });
    setErrors({ ...errors, [field]: "" });
  };

  const validate = () => {
    const newErrors: any = {};

    if (!form.name) newErrors.name = "Name is required";
    else if (!form.name.trim()) newErrors.name = "Name cannot be only spaces";

    if (!form.email) newErrors.email = "Email is required";
    if (!form.password) newErrors.password = "Password is required";
    if (!form.confirmPassword) newErrors.confirmPassword = "Confirm password is required";

    if (form.password && form.password.length < 6)
      newErrors.password = "Password must be at least 6 characters";

    if (form.password && form.confirmPassword && form.password !== form.confirmPassword)
      newErrors.confirmPassword = "Passwords do not match";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const authMutation = useMutation(
    (form: LoginPayload) => {
      return loginUser(form.email, form.password, 'DEFAULT');
    },
    {
      onSuccess: (response: any) => {
        setAuth(response.result.result);
        localStorage.setItem("joinQualPopup", "true");
        localStorage.setItem("access_token", response.token);
        setLoading(false);
        // ✅ delay for cookie sync (IMPORTANT)
        setTimeout(() => {
          navigate("/dashboard");
        }, 100);
      },
      onError: (err: any) => {
        const repsonse = err.response as any;
        toastrError(repsonse?.message);
        setLoading(false);
      },
    }
  );

  const userRegisteration = useMutation(
    (form: formValue) => {
      return registerUserNew({
        password: form.password,
        confirmPassword: form.confirmPassword,
        name: form.name,
        email: form.email,
        referalId: form.referalId,
        language: form.language,
        timeZone: form.timeZone,
        currentUrl: form.currentUrl,
        sid: form.sid,
        clickid: form.clickid,
        geo: form.geo,
        country: form.country,
        state: form.state,
        countryname: countryname,
        ipAddress: ipAddress,
        sessionId: form.sessionId,
        uid: form.uid,
        isGuest: isGuest,
        altcha: form.altcha,
        contactReason,
        marketingConsent,
      });
    },
    {
      onSuccess: (response: any) => {
        localStorage.setItem("access_token", response.token);
        if (response.result.success) {
          toastrSuccess(response.result.message);
          authMutation.mutate(form);
          // setShowJoinQualification(true);
        } else {
          toastrError(response.result.message);
          setLoading(false);
        }
      },
      onError: (err: AxiosError) => {
        const repsonse = err.response as AxiosResponse<ErrorBody, Record<string, unknown>>;
        if (repsonse.data && repsonse.data.message) {
          toastrError(repsonse.data.message);
        } else {
          toastrError('Something went wrong please try again!');
        }
        setLoading(false);
      },
    }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    if (!validate()) {
      setLoading(false);
      return;
    }
    if (!isEmailVerified) {
      setErrors({ ...errors, email: "Please verify your email" });
      setLoading(false);
      return;
    }

    // Fresh single-use challenge, solved immediately before the request.
    const altcha = await solveAltcha();
    if (!altcha) {
      toastrError("Captcha verification failed. Please try again.");
      setLoading(false);
      return;
    }

    userRegisteration.mutate({ ...form, altcha });
  };

  return (
    <>
      <AuthLayout aside={isGuest === 1 ? <TerminateImageComponent /> : <ImageComponent />}>
        <AuthCard
          eyebrow={isGuest === 1 ? "Join Zamplia Surveys" : "Get started"}
          title="Create your account"
          subtitle="Turn your free time into extra income with quick surveys."
          footer={
            <p>
              Already have an account?{" "}
              <Link to={"/login"} className="zs-link">
                Sign in
              </Link>
            </p>
          }
        >
          {/* Google Button */}
          <JoinNowWithGoogle marketingConsent={marketingConsent} />

          <AuthDivider label="or sign up with email" />

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <FloatingLabelInput
                label="Name*"
                value={form.name}
                onChange={handleChange("name")}
              />
              {errors.name && <p className="zs-error">{errors.name}</p>}
            </div>
            <div className="pt-1">
              <FloatingLabelInput
                label="Email*"
                type="email"
                value={form.email}
                onChange={handleChange("email")}
                isEmailVerified={setIsEmailVerified}
              />
              {errors.email && <p className="zs-error">{errors.email}</p>}
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-3">
              <div>
                <FloatingLabelInput
                  label="Create Password*"
                  type="password"
                  value={form.password}
                  onChange={handleChange("password")}
                />
                {errors.password && <p className="zs-error">{errors.password}</p>}
              </div>
              <div>
                <FloatingLabelInput
                  label="Confirm Password*"
                  type="password"
                  value={form.confirmPassword}
                  onChange={handleChange("confirmPassword")}
                />
                {errors.confirmPassword && <p className="zs-error">{errors.confirmPassword}</p>}
              </div>
            </div>

            <div className="rounded border border-line bg-surface-2 p-3.5">
              <MarketingConsentCheckbox
                checked={marketingConsent}
                onChange={setMarketingConsent}
              />
            </div>

            <div className="flex justify-center">{altchaWidget}</div>
            <Honeypot value={contactReason} onChange={setContactReason} />

            {isGuest === 0 && (
              <p className="text-xs leading-5 text-ink-3">
                By signing up, I agree to the Zamplia Surveys{" "}
                <Link to="/terms-and-conditions" className="zs-link">
                  Participant Terms and Conditions
                </Link>{" "}
                and{" "}
                <Link to="/privacy-policy" className="zs-link">
                  Privacy Policy
                </Link>
                .
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="zs-btn zs-btn-primary zs-btn-lg zs-btn-block"
            >
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </form>
        </AuthCard>
      </AuthLayout>
    </>
  );
};
